import React, { useState } from 'react'
import ActionButton from '@jimmyboys/liita-ui/dist/components/ActionButton'
import Dropdown from '@jimmyboys/liita-ui/dist/components/Dropdown'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import TextInput from '@jimmyboys/liita-ui/dist/components/TextInput'
import { useFetchList } from 'store'

import styles from './Dialog.module.sass'
import { API_URL, fetchOptions, parseJSONContent } from 'store/fetchUtils'
import { ClinicalProcess, Options } from 'models'
import { useDeepCompareEffect } from 'react-use'
import { useReload } from 'store'
import useSession from 'hooks/useSession'
import TaskHeaderErrors from 'components/TaskHeaderErrors/TaskHeaderErrors'
import { translateErrorMessage } from 'hooks/useSubmitTask'

const getCoordinatorConfig = (processId, i18n) => ({
	general: {
		endpoint: `/clinical_processes/${processId}/clinical_coordinators`,
		type: 'ClinicalCoordinator',
		dataProperty: 'clinicCoordinator',
		userLabel: i18n`Doctor`,
	},
	ipp: {
		endpoint: `/clinical_processes/${processId}/final_situation_prevision_coordinators`,
		type: 'FinalPrevisionCoordinator',
		dataProperty: 'ippCoordinator',
		userLabel: i18n`Doctor`,
	},
	trust_manager: {
		endpoint: '/metadata/all_trust_managers',
		type: 'AdministrativeCoordinator',
		dataProperty: 'trustClaimsManager',
		userLabel: i18n`Trust manager`,
	},
})

const TransferCoordinatorDialog = (props) => {
	const { className, data, resolve, dismiss } = props
	const { i18n, expireSession, pushNotification } = useGlobal()

	const [coordinator, setCoordinator] = useState(null)
	const [bodyPart, setBodyPart] = useState(null)
	const [injuryType, setInjuryType] = useState(null)
	const [observation, setObservation] = useState('')
	const [submitting, setSubmitting] = useState(false)
	const { user: sessionUser } = useSession()
	const [error, setError] = useState(null)
	const reload = useReload()
	const coordinatorConfig =
		getCoordinatorConfig(data.processId, i18n)[data.coordinatorType] ?? {}

	const {
		contents: [processData, bodyParts, injuries, availableDoctors],
	} = useFetchList(
		[
			`/clinical_processes/${data.processId}`,
			'metadata/body_parts',
			'metadata/injuries_types',
			coordinatorConfig.endpoint,
		],
		[
			ClinicalProcess.fromJSON,
			Options.fromSimpleObjectJSON,
			Options.fromSimpleObjectJSON,
			Options.fromSimpleObjectJSON,
		]
	)

	const isAdmin = sessionUser?.roles.includes('admin')

	const processedAvailableDoctors = !isAdmin
		? (availableDoctors || []).some(
				(f) => f.value === sessionUser?.username
		  )
			? availableDoctors
			: [
					{
						value: sessionUser?.username,
						text: `${sessionUser?.firstName} ${sessionUser?.lastName}`,
					},
					...(availableDoctors || []),
			  ]
		: availableDoctors

	processedAvailableDoctors?.sort((a, b) => a.text.localeCompare(b.text))

	useDeepCompareEffect(() => {
		if (processData) {
			setCoordinator(
				processData?.[coordinatorConfig.dataProperty]?.id ||
					availableDoctors[0]?.value
			)
			setBodyPart(
				processData?.info?.bodyPartId || bodyParts?.[0]?.value || ''
			)
			setInjuryType(
				processData?.info?.injuryTypeId || injuries?.[0]?.value || ''
			)
		} else {
			setBodyPart(bodyParts?.[0]?.value || '')
			setInjuryType(injuries?.[0]?.value || '')
		}
	}, [processData, bodyParts, injuries])

	const handleSubmit = (e) => {
		e.preventDefault()
		if (submitting) {
			return
		}
		const url = `${API_URL}/api/clinical_processes/${data?.processId}/transfer_coordinator`
		const coordinationType = coordinatorConfig.type

		const body = {
			coordinatorId: coordinator,
			coordinationType,
			bodyPartId: parseInt(bodyPart, 10),
			injuryTypeId: parseInt(injuryType, 10),
			observation,
		}

		setError(null)
		setSubmitting(true)
		fetch(
			url,
			fetchOptions({
				method: 'PUT',
				body,
			})
		)
			.then((r) => parseJSONContent(r))
			.then(() => {
				reload([`/clinical_processes/${data?.processId}`])
				resolve && resolve()
				pushNotification(
					undefined,
					'success',
					i18n`Process transfered!`
				)
			})
			.catch((err) => {
				if (err.status === '401' || err.status === 401) {
					expireSession()
				}

				const errorMessage = translateErrorMessage(err, i18n)

				setSubmitting(false)
				setError([errorMessage])
			})
	}

	return (
		<>
			<TaskHeaderErrors errors={error || []} />
			<form
				id={'form_transfer_coordinator'}
				className={`${styles['root'] || ''} ${className || ''}`}
				onSubmit={handleSubmit}
				disabled={submitting}
			>
				<fieldset>
					<small className={styles['dialog-title']}>
						{i18n`Choose the colleague you want to assign`}
					</small>
					<Dropdown
						placeholder={coordinatorConfig.userLabel}
						value={coordinator}
						options={processedAvailableDoctors || []}
						required
						displayValue={true}
						searchable={true}
						onChange={(e) => {
							setCoordinator(e.target.value)
						}}
					/>
					{data?.coordinatorType !== 'trust_manager' && (
						<>
							<Dropdown
								placeholder={i18n`Body Part`}
								value={bodyPart}
								options={bodyParts || []}
								displayValue={true}
								searchable={true}
								onChange={(e) => {
									setBodyPart(e.target.value)
								}}
							/>
							<Dropdown
								placeholder={i18n`Type of Injury`}
								value={injuryType}
								options={injuries || []}
								displayValue={true}
								searchable={true}
								onChange={(e) => {
									setInjuryType(e.target.value)
								}}
							/>
							<TextInput
								placeholder={i18n`Observations`}
								required
								value={observation}
								multiline
								noResize
								rows='3'
								onChange={(e) => {
									setObservation(e.target.value)
								}}
							/>
						</>
					)}
				</fieldset>
			</form>
			<menu>
				<ActionButton type='button' secondary onClick={dismiss}>
					<small>
						<strong>{i18n`Cancel`}</strong>
					</small>
				</ActionButton>
				<ActionButton form={'form_transfer_coordinator'} primary>
					<small>
						<strong>
							{submitting ? i18n`Submitting` : i18n`Send`}
						</strong>
					</small>
				</ActionButton>
			</menu>
		</>
	)
}

export default TransferCoordinatorDialog
